import { useMemo, useRef, useState } from "react";
import {
  MDSMenuInputHeader,
  MDSIconKind,
  MDSSelectMenu,
  MDSSelectMenuItem,
  MDSMenuItemIconVariant,
  MDSMenuItemSelectionVariant,
  MDSMenuDropdown,
  MDSMenuDropdownSlotName,
  MDSToggleButton,
} from "@/mds";
import { pluralize } from "@/modules/pluralize";
import { ContactsFacetFilterItem } from "./contacts-facet-filter.types";
import { ContactsFacetFilterNoResults } from "./contacts-facet-filter-no-results";
import { AccountProfileImage } from "@/components/layout/components/account-profile/account-profile-image";

interface ContactsFacetFilterProps {
  buttonLabel?: string;
  buttonPrefix?: string;
  searchText: string;
  onSearchTextChange: (value: string) => void;
  onSearchTextClear: () => void;
  items: ContactsFacetFilterItem[];
  onSelectItem: (id: string) => void;
  onSelectClear: () => void;
}

export const ContactsFacetFilter = ({
  buttonLabel,
  buttonPrefix,
  items,
  searchText,
  onSearchTextChange,
  onSearchTextClear,
  onSelectItem,
  onSelectClear,
}: ContactsFacetFilterProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectedItems = items.filter(item => item.isSelected);
  const hasSelectedItems = selectedItems.length > 0;
  const dropdownListRef = useRef<HTMLDivElement>(null);

  const computedButtonLabel = useMemo(() => {
    const count = selectedItems.length;
    if (buttonLabel) {
      return buttonLabel;
    }

    if (buttonPrefix) {
      if (selectedItems.length === 1) {
        return `${buttonPrefix} ${selectedItems[0].displayName}`;
      }

      if (count > 1) {
        return `${buttonPrefix} ${selectedItems[0].displayName} and ${count - 1} ${pluralize(count - 1, "other", "others")}`;
      }

      return `${buttonPrefix}`;
    }

    if (!count) {
      return "Contacts";
    }

    return `${items.filter(item => item.isSelected).length} Contacts`;
  }, [items, buttonLabel, buttonPrefix, selectedItems]);

  const visibleItems = items.filter(item => item.isVisible);

  return (
    <MDSMenuDropdown data-test-id="contacts-facet-filter" onOpenChange={setIsOpen}>
      <MDSToggleButton
        slot={MDSMenuDropdownSlotName.Trigger}
        isOpen={isOpen}
        iconKind={MDSIconKind.User}
        isToggled={hasSelectedItems}
        label={computedButtonLabel}
        onClear={() => onSelectClear()}
      />
      <div slot={MDSMenuDropdownSlotName.Menu}>
        <MDSMenuInputHeader
          onChange={onSearchTextChange}
          onClear={onSearchTextClear}
          value={searchText}
          onKeyDirection={() => {
            if (dropdownListRef.current) dropdownListRef.current.focus();
          }}
          onEscape={() => {
            if (searchText) onSearchTextChange("");
            else dropdownListRef.current?.focus();
          }}
          placeholder="Filter"
        />
        <MDSSelectMenu
          ref={dropdownListRef}
          aria-label="Filter by contacts"
          selectionBehavior="toggle"
          selectionMode="single"
          shouldFocusWrap={true}
          style={{ minWidth: 176, maxHeight: "50vh", maxWidth: 320 }}
        >
          {visibleItems.map(item => {
            return (
              <MDSSelectMenuItem
                key={item.id}
                id={item.id}
                label={item.displayName}
                subtitle={item.emailAddress}
                icon={() => (
                  <AccountProfileImage
                    photoUrl={item.photoUrl}
                    displayName={item.displayName}
                    size={24}
                  />
                )}
                iconVariant={MDSMenuItemIconVariant.Avatar}
                onClick={() => {
                  onSelectItem(item.id);
                }}
                isSelected={item.isSelected}
                selectionVariant={MDSMenuItemSelectionVariant.RadioButton}
              />
            );
          })}
          {visibleItems.length === 0 && <ContactsFacetFilterNoResults searchText={searchText} />}
        </MDSSelectMenu>
      </div>
    </MDSMenuDropdown>
  );
};
